import React from 'react';
import { Dialog } from '@base-ui-components/react/dialog';

// Modal wrapper over Base UI Dialog, keeping react-bootstrap's Modal API
// (show / onHide / bsSize, Modal.Header|Title|Body|Footer) so call sites
// migrate with an import swap. Base UI owns focus trap, scroll lock, esc
// and backdrop-click dismissal; all styling is ours (ui.css).

const SIZE_CLASS = { large: 'ui-modal--lg', lg: 'ui-modal--lg', small: 'ui-modal--sm', sm: 'ui-modal--sm' };

// Popup element of the nearest open Modal. Popup-owning components (ui
// DropdownButton) portal into this instead of <body>: a modal Dialog's focus
// trap treats focus moving to a body-level portal as "focus left the dialog"
// and force-closes the popup, so menus inside modals must stay in the
// dialog's DOM subtree.
export const ModalPopupContext = React.createContext(null);

// Portal container for popups (menus, poppers, tooltips) rendered by
// components that may sit inside a ui Modal. Returns the nearest open
// Modal's popup element, or undefined outside modals (Base UI then uses its
// default <body> portal). Every popup-owning component should pass this to
// its Portal/Popper `container` — omitting it doesn't error, but a
// body-portaled popup inside a modal breaks focus and dismissal handling.
export function useModalContainer() {
  const modalPopup = React.useContext(ModalPopupContext);
  return modalPopup ?? undefined;
}

/**
 * @param {object} props
 * @param {boolean} [props.show=false] Controlled visibility.
 * @param {() => void} [props.onHide] Fires once per dismissal (×, Esc on the
 *   topmost modal, or backdrop click).
 * @param {'large'|'lg'|'small'|'sm'} [props.bsSize] Width preset; omit for default.
 * @param {'large'|'lg'|'small'|'sm'} [props.size] Alias for bsSize.
 * @param {string} [props.dialogClassName] Extra class on the dialog popup.
 * @param {boolean} [props.enforceFocus=true] Set false on a modal that opens
 *   another ui Modal on top of itself (see stacking note above).
 */
const Modal = ({ show = false, onHide, bsSize, size, dialogClassName, className, enforceFocus = true, children, ...rest }) => {
  const sizeClass = SIZE_CLASS[bsSize || size];
  const classes = ['ui-modal', sizeClass, dialogClassName, className].filter(Boolean).join(' ');
  const [popupElement, setPopupElement] = React.useState(null);
  const popupRef = React.useRef(null);
  const assignPopup = React.useCallback((el) => {
    popupRef.current = el;
    setPopupElement(el);
  }, []);

  return (
    <Dialog.Root
      open={show}
      // enforceFocus={false} (react-bootstrap semantics) disables the focus
      // trap so a second modal can stack on top as a sibling portal.
      modal={enforceFocus}
      // Pointer dismissal is handled exclusively by the backdrop onClick
      // below. Base UI's own outside-press detection treats presses inside
      // sibling-portaled popups stacked above this dialog — and even the top
      // modal's backdrop — as "outside" and would close the dialogs
      // underneath along with the top one.
      disablePointerDismissal
      onOpenChange={(open, eventDetails) => {
        // Only the topmost ui Modal responds to Esc (react-bootstrap
        // semantics). Stacked modals are sibling portals, which Base UI does
        // not count as nested, so each one registers its own document-level
        // escape listener and a single press would close all of them.
        if (!open && eventDetails?.reason === 'escape-key' && popupRef.current) {
          const popups = Array.from(document.querySelectorAll('.ui-modal'));
          if (popups.indexOf(popupRef.current) !== popups.length - 1) return;
        }
        if (!open && onHide) onHide();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop
          className="ui-modal-backdrop"
          // Nested dialogs render no backdrop by default (Base UI skips
          // them); force one so every modal owns a dismissal surface.
          forceRender
          // The only pointer-dismissal path (Base UI's is disabled above, so
          // this cannot double-invoke onHide). Each modal has its own
          // backdrop and a click lands on the topmost one, so only that
          // modal closes.
          onClick={() => {
            if (show && onHide) onHide();
          }}
        />
        <Dialog.Popup
          className={classes}
          ref={assignPopup}
          // react-bootstrap parity: focus the dialog itself on open, not the
          // first tabbable element. Base UI's default autofocuses the first
          // input, which flips focus-dependent UI (EditableName's count
          // postfix, Edit/Done toggles) the moment the modal opens.
          initialFocus={popupRef}
          {...rest}
        >
          <ModalPopupContext.Provider value={popupElement}>
            {children}
          </ModalPopupContext.Provider>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const ModalHeader = ({ closeButton, className, children, ...rest }) => (
  <div className={['ui-modal__header', className].filter(Boolean).join(' ')} {...rest}>
    {closeButton && (
      <Dialog.Close className="ui-modal__close" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </Dialog.Close>
    )}
    {children}
  </div>
);

const ModalTitle = ({ className, children, ...rest }) => (
  <Dialog.Title className={['ui-modal__title', className].filter(Boolean).join(' ')} {...rest}>
    {children}
  </Dialog.Title>
);

const ModalBody = ({ className, children, ...rest }) => (
  <div className={['ui-modal__body', className].filter(Boolean).join(' ')} {...rest}>
    {children}
  </div>
);

const ModalFooter = ({ className, children, ...rest }) => (
  <div className={['ui-modal__footer', className].filter(Boolean).join(' ')} {...rest}>
    {children}
  </div>
);

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
